// src/context/LoginContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { getCookieSuffix, getLoginUrl } from '../utils/resolveEnv';
export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastRefreshRef = useRef(null);
  const hadRefresh = useRef(false);

  // 👇 Función para procesar login desde cookie
  const checkLogin = async (raw) => {
    try {
      if (!raw) {
        console.warn('❌ Cookie refresh no presente.');
        setIsLoggedIn(false);
        setUserData(null);
        setIsLoading(false);
        return;
      }

      if (raw === lastRefreshRef.current) {
        //        console.log('⏸ Cookie ya validada. No se repite.');
        setIsLoading(false);
        return;
      }

      // Verificar si ya existe un token válido y datos de usuario
      const existingToken = localStorage.getItem('token');
      const existingUserData = localStorage.getItem('userData');
      if (existingToken && existingUserData) {
        try {
          const userInfo = JSON.parse(existingUserData);
          setIsLoggedIn(true);
          setUserData(userInfo);
          setIsLoading(false);
          lastRefreshRef.current = raw;
          return;
        } catch (err) {
          // Si hay error parseando, continuar con el flujo normal
        }
      }

      lastRefreshRef.current = raw;
      //      console.log('🍪 Cookie decodificada:', raw);

      const refreshObj = JSON.parse(raw);
      const { refresh, access, timestamp, email } = refreshObj;

      if (!refresh || !access || !timestamp || !email) {
        console.warn('⚠️ La cookie no tiene todos los campos necesarios:', refreshObj);
        setIsLoggedIn(false);
        setUserData(null);
        setIsLoading(false);
        return;
      }

      const payload = { refresh, access, timestamp };
      //      console.log('🔐 Creando JWT con payload:', payload);

      const jwtRefresh = await createSignedJWT(payload, access);
      //     console.log('✅ JWT firmado:', jwtRefresh);

      // Obtener persist de la cookie params
      const persist = getPersistFromParams();

      let loginUrl;
      const hostname = window.location.hostname;

      loginUrl = getLoginUrl(hostname);

      const requestData = { refresh: jwtRefresh, user: email };
      if (persist !== undefined) requestData.persist = persist;

      const res = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });


      const data = await res.json();
      //      console.log('📬 Respuesta del backend:', data);

      if (data.access && data.login) {
        localStorage.setItem('token', data.access);
        localStorage.setItem('refreshToken', data.refresh);

        const userInfo = {
          email: data.login.email,
          name: data.login.name,
          role: data.login.roleName,
          uuid: data.login.uuid,
          access: data.access,
          config: data.login.config,
        };

        localStorage.setItem('userData', JSON.stringify(userInfo));
        console.log('✅ Usuario autenticado:', userInfo);
        console.log('🔧 Config específico - apiEnabled:', userInfo.config?.apiEnabled);
        console.log('🔧 Config específico - formulasEnabled:', userInfo.config?.formulasEnabled);
        setIsLoggedIn(true);
        setUserData(userInfo);
      } else {
        console.warn('❌ La respuesta no contiene access o login.');
        setIsLoggedIn(false);
        setUserData(null);
      }
    } catch (err) {
      console.error('💥 Error crítico en checkLogin:', err);
      setIsLoggedIn(false);
      setUserData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 👀 Detecta aparición o desaparición de la cookie `refresh`
  useEffect(() => {
    let lastSeenCookie = null;

    const detectRefreshChange = () => {
      const hostname = window.location.hostname;

      const cookieName = `refresh${getCookieSuffix(hostname)}`;


      const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${cookieName}=([^;]+)`));
      const current = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;


      if (current && current !== lastSeenCookie) {
        //        console.log('🆕 Cookie refresh apareció o cambió.');
        lastSeenCookie = current;
        hadRefresh.current = true;
        checkLogin(current);
      }

      if (!current && hadRefresh.current) {
        //       console.log('❌ Cookie refresh desapareció. Recargando...');
        window.location.reload();
      }
      //    console.log('🧪 Estado actual de cookies:', document.cookie);
    };

    const interval = setInterval(detectRefreshChange, 1000);
    return () => clearInterval(interval);
  }, []);

  // 📡 Escucha logout entre subdominios
  useEffect(() => {
    const handleLogoutEvent = (e) => {
      if (e.key === 'logout-event') {
        //       console.log('🔐 Logout detectado desde otro subdominio.');
        setIsLoggedIn(false);
        setUserData(null);
        lastRefreshRef.current = null;
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleLogoutEvent);
    return () => window.removeEventListener('storage', handleLogoutEvent);
  }, []);

  return (
    <LoginContext.Provider value={{ isLoggedIn, userData }}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLogin = () => useContext(LoginContext);

// Obtener persist de la cookie params
const getPersistFromParams = () => {
  const hostname = window.location.hostname;
  const cookieName = `params${getCookieSuffix(hostname)}`;

  const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${cookieName}=([^;]+)`));
  if (cookieMatch) {
    try {
      const paramsObj = JSON.parse(decodeURIComponent(cookieMatch[1]));
      return paramsObj.persist;
    } catch (err) {
      return undefined;
    }
  }
  return undefined;
};

// 🔏 Función para crear JWT firmado
async function createSignedJWT(payload, secret) {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const base64Url = (obj) =>
    btoa(JSON.stringify(obj)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');

  const encodedHeader = base64Url({ alg: 'HS256', typ: 'JWT' });
  const encodedPayload = base64Url(payload);
  const data = `${encodedHeader}.${encodedPayload}`;

  const signatureBuffer = await crypto.subtle.sign('HMAC', key, encoder.encode(data));
  const signatureArray = new Uint8Array(signatureBuffer);
  const signature = btoa(String.fromCharCode(...signatureArray))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

  //  console.log('🔏 JWT completo generado');
  return `${data}.${signature}`;
}
